import { fetchConversations } from "./api";
import type { Conversation } from "./types";

/** Fan out per-subject /v1/conversations queries in parallel, merge,
 *  sort newest-first by last_active_at, slice top N. Per-subject
 *  errors are swallowed — partial results beat a blocking error in
 *  the sidebar's Recents section.
 *
 *  Sized for the tenant.subjects shape (≤3 subjects today). If a
 *  tenant grows past ~10 subjects this should batch or move server-
 *  side; not a v1 concern. */
export async function fetchRecentsAcrossSubjects(
  authHeader: Record<string, string>,
  subjectAgentIds: readonly string[],
  limit = 15,
): Promise<Conversation[]> {
  const results = await Promise.allSettled(
    subjectAgentIds.map((id) => fetchConversations(authHeader, id, limit)),
  );
  const merged = results.flatMap((r) =>
    r.status === "fulfilled" ? r.value : [],
  );
  // Dedupe by conversation id — Nova OS's per-agent list endpoints
  // can return the same conversation for multiple agent buckets when
  // a single thread bridged across agents, and Charles flagged that
  // the recents panel was rendering the same id multiple times.
  // Keep the row whose last_active_at is newest; the rest are
  // stale snapshots of the same thread.
  const byId = new Map<string, Conversation>();
  for (const c of merged) {
    const prev = byId.get(c.id);
    if (
      !prev ||
      new Date(c.last_active_at).getTime() >
        new Date(prev.last_active_at).getTime()
    ) {
      byId.set(c.id, c);
    }
  }
  const dedup = [...byId.values()];
  dedup.sort(
    (a, b) =>
      new Date(b.last_active_at).getTime() -
      new Date(a.last_active_at).getTime(),
  );
  return dedup.slice(0, limit);
}
